import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Image,
  IconButton,
  Box,
  HStack,
  Text,
  IconButton as ChakraIconButton,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, AddIcon, MinusIcon } from '@chakra-ui/icons';

export default function CommentImagePreviewModal({ isOpen, onClose, imageUrl, onPrevious, onNext, hasMultiple }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      setImageSize({
        width: imageRef.current.naturalWidth,
        height: imageRef.current.naturalHeight
      });
    }
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  }, [scale, position]);

  const handleMouseMove = useCallback((e) => {
    e.preventDefault();
    
    if (isDragging && scale > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      const maxX = (imageSize.width * scale - imageSize.width) / 2;
      const maxY = (imageSize.height * scale - imageSize.height) / 2;
      
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    }
  }, [isDragging, scale, dragStart, imageSize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [imageUrl]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.5, 1));
    if (scale <= 1.5) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleClose = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent maxW="95vw" maxH="95vh" bg="gray.900">
        <Box position="absolute" right={2} top={2} zIndex={5}>
          <ModalCloseButton
            color="white" 
            onClick={handleClose}
          />
        </Box>
        <ModalBody p={0} display="flex" bg="gray.900">
          <Box 
            flex="1"
            h="95vh" 
            position="relative"
            overflow="hidden"
          >
            {/* Zoom Controls */}
            <HStack
              position="absolute" 
              top={4} 
              left={4} 
              spacing={4}
              zIndex={3}
            >
              <HStack
                bg="whiteAlpha.200" 
                p={2} 
                borderRadius="md"
                spacing={3}
              >
                <Text color="white" fontSize="sm">Zoom:</Text>
                <HStack spacing={1}>
                  <ChakraIconButton
                    icon={<MinusIcon />}
                    onClick={handleZoomOut}
                    isDisabled={scale <= 1}
                    size="sm"
                    colorScheme="blue"
                    aria-label="Zoom out"
                  />
                  <Text color="white" fontSize="sm" fontWeight="bold" minW="45px" textAlign="center">
                    {Math.round(scale * 100)}%
                  </Text>
                  <ChakraIconButton
                    icon={<AddIcon />}
                    onClick={handleZoomIn}
                    isDisabled={scale >= 3}
                    size="sm"
                    colorScheme="blue"
                    aria-label="Zoom in"
                  />
                </HStack>
              </HStack>
            </HStack>

            {/* Image */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              onMouseDown={handleMouseDown}
              cursor={isDragging ? "grabbing" : scale > 1 ? "grab" : "default"}
              userSelect="none"
            >
              <Box
                position="relative"
                style={{
                  transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                  transition: isDragging ? "none" : "transform 0.2s",
                  userSelect: 'none'
                }}
              >
                <Image
                  ref={imageRef}
                  src={imageUrl}
                  alt="Full size preview"
                  maxW="100%"
                  maxH="90vh"
                  objectFit="contain"
                  onLoad={handleImageLoad}
                  draggable={false}
                  style={{
                    WebkitUserDrag: 'none',
                    userSelect: 'none',
                    pointerEvents: scale > 1 ? 'none' : 'auto'
                  }}
                />
              </Box>
            </Box>

            {/* Navigation Controls */}
            {hasMultiple && (
              <>
                <IconButton
                  icon={<ChevronLeftIcon boxSize={8} />}
                  position="absolute"
                  left={4}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={onPrevious}
                  bg="blackAlpha.700"
                  color="white"
                  _hover={{ bg: "blackAlpha.800" }}
                  size="lg"
                  isRound
                  aria-label="Previous image"
                  zIndex={3}
                />
                <IconButton
                  icon={<ChevronRightIcon boxSize={8} />}
                  position="absolute"
                  right={4}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={onNext}
                  bg="blackAlpha.700"
                  color="white"
                  _hover={{ bg: "blackAlpha.800" }}
                  size="lg"
                  isRound
                  aria-label="Next image"
                  zIndex={3}
                />
              </>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
